import { useRef } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import {
  CheckCircle,
  Users,
  Target,
  Lightbulb,
  Award,
  Zap,
  Heart,
  ArrowRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";

const stats = [
  { value: "8+", label: "Years in Business", icon: Award },
  { value: "50+", label: "Team Members", icon: Users },
  { value: "200+", label: "Projects Delivered", icon: Target },
  { value: "99%", label: "Client Satisfaction", icon: Heart },
];

const team = [
  {
    name: "Prabhu",
    role: "CEO & Co-Founder",
    avatar: "/prabhu.jpeg",
  },
  {
    name: "Ananya Namdev",
    role: "CTO & Co-Founder",
    avatar: "/ananya2.jpeg",
  },
  {
    name: "Abhishek Sharma",
    role: "COO & Co-Founder",
    avatar: "/abhishek.jpeg",
  },
];

const values = [
  {
    icon: Zap,
    title: "Innovation First",
    desc: "We push boundaries and embrace emerging technologies to deliver cutting-edge solutions.",
  },
  {
    icon: Heart,
    title: "Client-Centric",
    desc: "Your success is our mission. We align every decision with your business goals.",
  },
  {
    icon: Target,
    title: "Quality Obsessed",
    desc: "Rigorous testing, code reviews, and best practices ensure exceptional output.",
  },
  {
    icon: Users,
    title: "Collaborative",
    desc: "Transparent communication and agile workflows keep everyone in sync.",
  },
  {
    icon: Lightbulb,
    title: "Continuous Learning",
    desc: "Our team constantly upskills to stay ahead of industry trends.",
  },
  {
    icon: Award,
    title: "Accountability",
    desc: "We own our commitments and deliver on every promise we make.",
  },
];

const timeline = [
  {
    year: "2017",
    title: "Founded",
    desc: "Softlume was born in a small New Delhi office with a team of 3 passionate engineers.",
  },
  {
    year: "2019",
    title: "First Enterprise Client",
    desc: "Landed our first Fortune 500 project, building a real-time analytics dashboard.",
  },
  {
    year: "2021",
    title: "Team of 30+",
    desc: "Expanded globally with talent across 5 countries and launched our mobile division.",
  },
  {
    year: "2023",
    title: "150+ Projects",
    desc: "Crossed the 150 project milestone with a 99% client satisfaction rate.",
  },
  {
    year: "2025",
    title: "AI & Beyond",
    desc: "Launched our AI consulting practice and expanded into cloud-native architecture.",
  },
];

export default function AboutPage() {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="about-page"
    >
      {/* Hero */}
      <section
        ref={heroRef}
        className="relative min-h-[60vh] flex items-center overflow-hidden pt-20"
      >
        <motion.div style={{ y: heroY }} className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/5511303/pexels-photo-5511303.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="About Softlume"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/85 to-slate-950" />
        </motion.div>
        <motion.div
          style={{ opacity: heroOpacity }}
          className="relative max-w-7xl mx-auto px-6 md:px-12 py-20"
        >
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-sm font-medium text-blue-400 tracking-widest uppercase mb-4 block"
          >
            About Us
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="font-heading font-extrabold text-5xl md:text-7xl tracking-tight text-white mb-6"
          >
            Our <span className="glow-text">Story</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-base md:text-lg text-slate-400 max-w-2xl leading-relaxed"
          >
            From a small team of dreamers to a global force in digital
            innovation, Softlume has always believed that great technology can
            change the world.
          </motion.p>
        </motion.div>
      </section>

      {/* Stats */}
      <StatsSection />

      {/* Mission */}
      <MissionSection />

      {/* Timeline */}
      <TimelineSection />

      {/* Values */}
      <ValuesSection />

      {/* Team */}
      <TeamSection />

      {/* CTA */}
      <section className="relative py-24 bg-slate-950">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />
        <div className="relative max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight text-white mb-6">
            Want to Join Our Team?
          </h2>
          <p className="text-base text-slate-400 mb-8">
            We're always looking for talented individuals who share our passion
            for innovation.
          </p>
          <Button
            data-testid="about-page-cta"
            onClick={() => navigate("/contact")}
            className="bg-blue-600 hover:bg-blue-500 text-white rounded-full px-8 h-12 text-base font-semibold hover:shadow-[0_0_30px_rgba(59,130,246,0.5)] group"
          >
            Get In Touch{" "}
            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </section>
    </motion.div>
  );
}

function StatsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <section className="relative py-16 bg-slate-950">
      <div ref={ref} className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.1 }}
              whileHover={{ y: -4 }}
              className="text-center p-6 rounded-2xl border border-white/5 bg-slate-900/30 hover:border-blue-500/20 transition-all duration-300"
            >
              <s.icon className="w-6 h-6 text-blue-400 mx-auto mb-3" />
              <div className="text-3xl font-heading font-bold text-white">
                {s.value}
              </div>
              <div className="text-sm text-slate-500 mt-1">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MissionSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const parallaxRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: parallaxRef,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "15%"]);

  return (
    <section
      ref={parallaxRef}
      className="relative py-24 bg-[#0a0f1e] overflow-hidden"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />
      <div ref={ref} className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
          >
            <span className="text-sm font-medium text-blue-400 tracking-widest uppercase mb-4 block">
              Our Mission
            </span>
            <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight text-white mb-6">
              Empowering Businesses Through{" "}
              <span className="gradient-text-blue">Technology</span>
            </h2>
            <p className="text-base text-slate-400 leading-relaxed mb-6">
              At Softlume, we believe technology should be an enabler, not a
              barrier. Our mission is to democratize access to world-class
              software development, making it possible for businesses of all
              sizes to compete in the digital age.
            </p>
            <p className="text-base text-slate-400 leading-relaxed">
              We invest heavily in our people, our processes, and our technology
              stack to ensure that every project we take on benefits from the
              collective wisdom of 8+ years of building successful digital
              products.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="relative rounded-2xl overflow-hidden aspect-video"
          >
            <motion.img
              style={{ y: imgY }}
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&auto=format&fit=crop&q=80"
              alt="Team collaborating"
              className="w-full h-[120%] object-cover -mt-[10%]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 to-transparent" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function TimelineSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <section className="relative py-24 bg-slate-950 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />
      <div ref={ref} className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          className="text-center mb-16"
        >
          <span className="text-sm font-medium text-blue-400 tracking-widest uppercase mb-4 block">
            Our Journey
          </span>
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight text-white">
            Timeline
          </h2>
        </motion.div>
        <div className="relative">
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-blue-500/30 via-blue-500/10 to-transparent" />
          {timeline.map((item, i) => (
            <motion.div
              key={item.year}
              initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.2 + i * 0.15, duration: 0.6 }}
              className={`relative flex items-start gap-6 mb-12 ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"} md:text-${i % 2 === 0 ? "right" : "left"}`}
            >
              <div className="hidden md:block flex-1" />
              <div className="absolute left-4 md:left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-blue-500 border-2 border-slate-950 z-10 shadow-[0_0_10px_rgba(59,130,246,0.5)]" />
              <div className="flex-1 pl-10 md:pl-0">
                <span className="text-sm font-bold text-blue-400">
                  {item.year}
                </span>
                <h4 className="font-heading font-semibold text-xl text-white mt-1">
                  {item.title}
                </h4>
                <p className="text-sm text-slate-400 mt-2">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ValuesSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <section className="relative py-24 bg-[#0a0f1e] overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />
      <div ref={ref} className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <span className="text-sm font-medium text-blue-400 tracking-widest uppercase mb-4 block">
            What Drives Us
          </span>
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight text-white">
            Our Core Values
          </h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {values.map((v, i) => (
            <motion.div
              key={v.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 + i * 0.1 }}
              whileHover={{ y: -6 }}
              className="p-6 rounded-2xl border border-white/5 bg-slate-900/30 hover:border-blue-500/20 transition-all duration-300 group"
            >
              <div className="w-11 h-11 rounded-xl bg-blue-500/10 flex items-center justify-center mb-4 group-hover:bg-blue-500/20 transition-colors">
                <v.icon className="w-5 h-5 text-blue-400" />
              </div>
              <h4 className="font-heading font-semibold text-lg text-white mb-2">
                {v.title}
              </h4>
              <p className="text-sm text-slate-400 leading-relaxed">{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TeamSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <section className="relative py-24 bg-slate-950 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />
      <div ref={ref} className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <span className="text-sm font-medium text-blue-400 tracking-widest uppercase mb-4 block">
            Our People
          </span>
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight text-white">
            Leadership Team
          </h2>
        </motion.div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {team.map((member, i) => (
            <motion.div
              key={member.name}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 + i * 0.08 }}
              whileHover={{ y: -6 }}
              className="text-center group"
            >
              <div className="relative w-24 h-24 mx-auto mb-3 rounded-2xl overflow-hidden border border-white/10 group-hover:border-blue-500/30 transition-colors">
                <img
                  src={member.avatar}
                  alt={member.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="text-sm font-medium text-white">
                {member.name}
              </div>
              <div className="text-xs text-slate-500">{member.role}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
